import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { Modal } from 'antd';
import { Mutex } from 'async-mutex';
import { parseJSONToFormData } from '@/utils/data-format';
import { filterReqParams } from '@/utils/filterReqParams';
import {
  getAccessToken,
  getRefreshToken,
  clearAccessToken,
  setRefreshToken as setLocalRefreshToken,
  setAccessToken as setLocalAccessToken,
} from '@/utils/auth';
import SERVER_CONFIG from '@/config/server';
import { setUser, setAccessToken, setRefreshToken } from './app';

const { REACT_APP_CLIENT_SECRET } = process.env;
const { OAUTH_SERVER } = SERVER_CONFIG;
const mutex = new Mutex();

// 验证失败跳转到首页
const goToLogin = (api) => {
  clearAccessToken();
  api.dispatch(setUser(''));
  return (
    Modal.warning({
      centered: true,
      content: '用户信息已过期，请重新登录！',
      okText: '重新登录',
      keyboard: false,
      onOk: () => {
        if (window.location.href.split('#')[1] === '/login') {
          window.location.reload();
        } else {
          window.location.href = `${window.location.href.split('#')[0]}#/login`;
          window.location.reload();
        }
      },
      width: 416,
    })
  );
};

/**
 * baseQuery相关的配置，默认获取Token
 */
let requestArg = {};
const options = {
  baseUrl: `${SERVER_CONFIG.BASE_URL}`,
  prepareHeaders(headers) {
    // headers各个切片中配置读取后生成的header，相当于是拦截，可在这里覆盖之前的配置
    if (!headers.get('Authorization')) {
      // const token = tokenSelector(getState());
      const token = getAccessToken(requestArg) || '';
      if (token) {
        headers.set('Authorization', token);
      }
    }
    return headers;
  },
  credentials: 'include',
  mode: 'cors',
};
const baseQuery = fetchBaseQuery(options);

// 获取新的token
const updataRefreshToken = async ({
  release, api, extraOption,
}) => {
  const refreshResult = await baseQuery({
    url: `${OAUTH_SERVER}/token`,
    method: 'POST',
    headers: {
      Authorization: REACT_APP_CLIENT_SECRET,
    },
    body: parseJSONToFormData({
      grant_type: 'refresh_token',
      refresh_token: getRefreshToken(),
    }),
  }, api, extraOption);
  if (refreshResult.data && !refreshResult.data.status) {
    const { data: { access_token: accessKoken, refresh_token: refreshToken, token_type: tokenType, expires_in: expiresIn } } = refreshResult;
    api.dispatch(setAccessToken(accessKoken));
    api.dispatch(setRefreshToken(refreshToken));
    setLocalRefreshToken(refreshToken);
    setLocalAccessToken(`${tokenType} ${accessKoken}`);
    localStorage.setItem('maxTime', Date.now() + expiresIn * 1000 - 1800000);
    // 将异步锁解开继续向下执行
    release();
  } else {
    goToLogin(api);
  }
};

const baseQueryWithInterceptor = retry(
  async (arg, api, extraOption) => {
    // 如果异步锁打开，等待
    await mutex.waitForUnlock();
    const maxTime = localStorage.getItem('maxTime');
    const nowTime = new Date().getTime();
    const token = getAccessToken(arg);
    const rft = localStorage.getItem('rft');
    // 判断登陆信息有没有过期并且存在 token 和 刷新token rft
    if (nowTime >= maxTime && !mutex.isLocked() && token && rft) {
      // 开启异步锁
      const release = await mutex.acquire();
      // 等待重新获取登录信息
      await updataRefreshToken({ release, api, extraOption });
    }
    // 等待异步锁开启，开启锁则等待获取新的token
    await mutex.waitForUnlock();
    const newArg = filterReqParams(arg);
    requestArg = arg;
    // 发送请求
    const result = await baseQuery(newArg, api, extraOption);
    const { meta } = result;

    // ---------------------------- 拦截

    if (newArg.interceptor) {
      return newArg.interceptor(result, { newArg, api, extraOption });
    }

    // ---------------------------- 错误处理

    // 按照js异步规范，错误处理一般放到最前
    // 处理响应错误 响应码指示为错误，但非鉴权失败(401)
    if (result.error) {
      const { error: { data } } = result;
      /*
      * A0207 账号角色失效
      * A0303 无效的请求
      * A0317 token失效
      * A0331 无效的刷新token REFRESH_TOKEN
      */
      if (data.status === 'A0317' || data.status === 'A0303' || data.status === 'A0207' || data.status === 'A0331') {
        goToLogin(api);
      }
      // 最后，将错误返回
      const { status: _status } = result.error;
      if (_status >= 400 && _status < 500) {
        // 响应鉴权失败
        retry.fail(result.error);
      }
      return result;
    }

    // ---------------------------- 数据格式处理
    // 如果error为宽松false，那么data为宽松true
    const { data } = result;
    // 如果响应体中业务状态码正常，省略一层判断，提取数据
    if (Object.is(data.status, '00000')) {
      return { data: data.data, meta };
    }
    // 否则，返回错误信息
    const _error = {
      status: meta.response.status,
      data,
    };
    retry.fail(_error);
    return {
      error: _error,
      meta,
    };
  },
  {
    maxRetries: 3,
  },
);

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithInterceptor,
  endpoints: () => ({}),
  keepUnusedDataFor: 60,
});

export default apiSlice.reducers;

export {
  baseQuery,
  // baseQueryWithRetry,
  baseQueryWithInterceptor,
};
